// `/activity` — how outbound is going. The default board for leadership, viewers and admin.
//
// Deliberately not a table of accounts. The questions this answers are funnel questions -- how
// many did we reach, how many answered, how many met us, did any become a deal -- and 422 rows
// is the wrong shape for every one of them. What is here instead is a funnel over a window, a
// four-row strategist table, and a trend; the accounts live one click deeper, in a drawer
// behind each stage.
//
// Everything on this page is windowed except one tile. "How much of the book has nobody
// touched" is a standing fact rather than something that happened this month, so it sits in the
// book strip and never moves when the window changes.

import { useCallback, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AccessDeniedError, fetchActivity } from '../api/client';
import type {
  ActivityPayload, ActivityWindowOption, FunnelStage, StrategistRow,
} from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { DeniedGate } from '../auth/Gate';
import { PageChrome } from '../components/PageChrome';
import { SectionHead } from '../components/SectionHead';
import { Funnel } from '../activity/Funnel';
import { StageDrawer } from '../activity/StageDrawer';
import { scopedVerification, verificationLabel } from '../activity/attendanceVerification';
import type { DrawerRequest } from '../activity/StageDrawer';
import { StrategistTable } from '../activity/StrategistTable';
import { defaultStrategist } from '../activity/scope';
import {
  findScopedRow, isScoutRow, laneSplitText, scopeMembers, scopeWeeks, tableRows,
} from '../activity/lanes';
import { Trend } from '../activity/Trend';
import { formatCount, formatMoney } from '../lib/format';

// A tile that opens the list behind its own number.
//
// Almost every big figure on this board is now a button, for the same reason every funnel stage
// already was: a number nobody can open is a number nobody can check. The three that are not are
// `Accounts` (the book, which the execution board lists properly) and the two rate-ish figures,
// where there is no list to show.
//
// A zero is still clickable, and the drawer says so in words. That matches the funnel, which has
// always opened at zero, and the board's standing rule that an empty number is a finding rather
// than a gap — "nothing here this period" is an answer, and a tile that goes dead when it hits
// zero teaches people to stop trusting the click.
function Tile({
  label,
  value,
  foot,
  drawer,
  total,
  accent,
  onOpen,
}: {
  label: string;
  value: string;
  foot: React.ReactNode;
  drawer?: string;
  total?: number;
  accent?: boolean;
  onOpen?: (key: string, total: number) => void;
}) {
  const openable = Boolean(drawer && onOpen);
  const body = (
    <>
      <div className="lab">{label}</div>
      <div className="val" style={accent && value !== '0' ? { color: 'var(--primary)' } : undefined}>
        {value}
      </div>
      <div className="foot">{foot}</div>
    </>
  );
  if (!openable) return <div className="tile">{body}</div>;
  return (
    <button
      type="button"
      className="tile open"
      onClick={() => onOpen!(drawer!, total ?? 0)}
      title="Open the accounts behind this number"
    >
      {body}
    </button>
  );
}

// The Manual / Scout line under a team tile. Only on the team view: a scoped tile is already one
// row's number. The hover says why the two can add up to more than the figure above them.
function LaneSplit({ payload, manual, scout }: { payload: ActivityPayload; manual: string; scout: string }) {
  return <div className="lanesplit" title={payload.scout_note}>{laneSplitText(manual, scout)}</div>;
}

// The Manual and Scout rows to split a team tile by, or null when scoped or on an older payload.
function teamLanes(payload: ActivityPayload, row: StrategistRow | null): { manual: StrategistRow; scout: StrategistRow } | null {
  if (row || !payload.manual || !payload.scout) return null;
  return { manual: payload.manual, scout: payload.scout };
}

// The account funnel in four figures: the book, and how far into it this period got.
//
// **Scoped to the selected strategist, like everything else on the page.** These four used to
// read team-wide whatever the picker said, so choosing Omair left "422 accounts" sitting above
// his own funnel — the one number on the board that ignored the scope, and therefore the one
// most likely to be quoted wrong. The scoped figures come off that person's own row rather than
// from a second request, the same way the funnel's do.
//
// `Accounts` is a standing fact about the book and the other three belong to the period, so each
// tile says which underneath itself rather than leaving the mix to be inferred.
function BookStrip({
  payload,
  row,
  onOpen,
}: {
  payload: ActivityPayload;
  row: StrategistRow | null;
  onOpen: (key: string, total: number) => void;
}) {
  const { book, totals, window } = payload;
  const accounts = row ? row.accounts_owned : book.accounts_owned;
  const contacted = row ? row.accounts_worked : book.accounts_worked;
  const inConversation = row ? row.accounts_replied : book.accounts_replied;
  const pipeline = row ? row.qualified_pipeline : totals.qualified_pipeline;
  const deals = row ? row.opportunities : totals.opportunities;
  const lanes = teamLanes(payload, row);
  return (
    <div className="strip">
      <Tile
        label="Accounts"
        value={formatCount(accounts)}
        foot={<>{row ? 'on their book' : 'with a strategist'} · all time
          {lanes ? <LaneSplit payload={payload} manual={formatCount(lanes.manual.accounts_owned)} scout={formatCount(lanes.scout.accounts_owned)} /> : null}</>}
      />
      <Tile
        label="Contacted"
        value={formatCount(contacted)}
        foot={<>outbound sent · {window.label}
          {lanes ? <LaneSplit payload={payload} manual={formatCount(lanes.manual.accounts_worked)} scout={formatCount(lanes.scout.accounts_worked)} /> : null}</>}
        drawer="reached"
        total={contacted}
        onOpen={onOpen}
      />
      <Tile
        label="In conversation"
        value={formatCount(inConversation)}
        foot={<>answered · {window.label}
          {lanes ? <LaneSplit payload={payload} manual={formatCount(lanes.manual.accounts_replied)} scout={formatCount(lanes.scout.accounts_replied)} /> : null}</>}
        drawer="replied"
        total={inConversation}
        onOpen={onOpen}
      />
      <Tile
        label="Qualified pipeline"
        value={pipeline ? formatMoney(pipeline)! : '$0'}
        foot={<>deals opened · {window.label}
          {lanes ? <LaneSplit payload={payload} manual={formatMoney(lanes.manual.qualified_pipeline) ?? '$0'} scout={formatMoney(lanes.scout.qualified_pipeline) ?? '$0'} /> : null}</>}
        drawer="pipeline"
        total={deals}
        accent
        onOpen={onOpen}
      />
    </div>
  );
}

// The two quota numbers. Accounts met is companies the strategist sat in a meeting with this
// period. Stage 2 is companies whose deal reached stage 2 or later this period within four
// weeks of such a meeting — one number, one rule, decided in activity.py.
function QuotaStrip({
  payload,
  row,
  onOpen,
}: {
  payload: ActivityPayload;
  row: StrategistRow | null;
  onOpen: (key: string, total: number) => void;
}) {
  const met = row ? row.accounts_met_attended : payload.quota.accounts_met_attended;
  const stage2 = row ? row.accounts_to_stage_2 : payload.quota.accounts_to_stage_2;
  const lanes = teamLanes(payload, row);
  return (
    <div className="strip quota">
      <Tile
        label="Accounts met"
        value={formatCount(met)}
        foot={<>{verificationLabel(scopedVerification(payload, row?.sdr_owner_email ?? null))}
          {lanes ? <LaneSplit payload={payload} manual={formatCount(lanes.manual.accounts_met_attended)} scout={formatCount(lanes.scout.accounts_met_attended)} /> : null}</>}
        drawer="met"
        total={met}
        onOpen={onOpen}
      />
      <Tile
        label="Stage 2"
        value={formatCount(stage2)}
        foot={<>reached stage 2+ this period, met within the prior 4 weeks
          {lanes ? <LaneSplit payload={payload} manual={formatCount(lanes.manual.accounts_to_stage_2)} scout={formatCount(lanes.scout.accounts_to_stage_2)} /> : null}</>}
        drawer="opportunity"
        total={stage2}
        onOpen={onOpen}
      />
    </div>
  );
}

// The three meeting numbers, which are three answers rather than one split three ways.
//
// `Booked` and `Attended` are the same meetings at two moments — never added — and the gap
// between them is the useful part: booked-with-none-attended is either meetings still to come or
// a month of no-shows. `Influenced` is the separate inference.
//
// The unique-account version of Attended is not repeated here; it is the quota tile at the top
// of the page, and printing it twice invites the two to be read as different numbers.
function MeetingStrip({
  payload,
  row,
  onOpen,
}: {
  payload: ActivityPayload;
  row: StrategistRow | null;
  onOpen: (key: string, total: number) => void;
}) {
  const { totals } = payload;
  const booked = row ? row.meetings_booked : totals.meetings_booked;
  const attended = row ? row.meetings_attended : totals.meetings_attended;
  const influenced = row ? row.meetings_influenced : totals.meetings_influenced;
  const lanes = teamLanes(payload, row);
  return (
    <div className="strip">
      <Tile
        label="Booked"
        value={formatCount(booked)}
        foot={<>scheduled in this period, whenever they sit
          {lanes ? <LaneSplit payload={payload} manual={formatCount(lanes.manual.meetings_booked)} scout={formatCount(lanes.scout.meetings_booked)} /> : null}</>}
        drawer="booked"
        total={booked}
        onOpen={onOpen}
      />
      <Tile
        label="Attended"
        value={formatCount(attended)}
        foot={<>{verificationLabel(scopedVerification(payload, row?.sdr_owner_email ?? null))}
          {lanes ? <LaneSplit payload={payload} manual={formatCount(lanes.manual.meetings_attended)} scout={formatCount(lanes.scout.meetings_attended)} /> : null}</>}
        drawer="attended"
        total={attended}
        accent
        onOpen={onOpen}
      />
      <Tile
        label="Influenced"
        value={formatCount(influenced)}
        foot={<>an AE’s meeting, booked after their first touch
          {lanes ? <LaneSplit payload={payload} manual={formatCount(lanes.manual.meetings_influenced)} scout={formatCount(lanes.scout.meetings_influenced)} /> : null}</>}
        drawer="influenced"
        total={influenced}
        onOpen={onOpen}
      />
    </div>
  );
}

// Who the board is about. A select rather than four more buttons: the window is a scale that
// gets compared across its options, and this is a scope that gets chosen once — different
// questions deserve different controls even when they sit side by side.
//
// Built from the payload's own strategist rows rather than from the accounts, so somebody who
// sent nothing this period is still in the list. A name that vanishes when its owner has a
// quiet month is the same bug as a missing table row.
function StrategistPicker({
  rows,
  value,
  onChange,
}: {
  rows: StrategistRow[];
  value: string | null;
  onChange: (email: string | null) => void;
}) {
  return (
    <div className="field">
      <label className="fieldlab" htmlFor="astrategist">Strategist</label>
      <select
        id="astrategist"
        value={value ?? ''}
        onChange={event => onChange(event.target.value || null)}
      >
        <option value="">All strategists</option>
        {/* The six strategists (roles.json, server-side), then Scout's automated lane apart from
            them: Scout is a lane of sends to scope by, not a strategist. */}
        <optgroup label="Strategists">
          {rows.filter(row => !isScoutRow(row)).map(row => (
            <option key={row.sdr_owner_email ?? row.sdr_owner} value={row.sdr_owner_email ?? ''}>
              {row.sdr_owner}
            </option>
          ))}
        </optgroup>
        {rows.filter(isScoutRow).map(row => (
          <optgroup key={row.sdr_owner_email ?? row.sdr_owner} label="Automated lane">
            <option value={row.sdr_owner_email ?? ''}>{row.sdr_owner}</option>
          </optgroup>
        ))}
      </select>
    </div>
  );
}

// The period buttons, as two controls rather than one row of ten.
//
// Months are the primary scale and read left to right as time moving forward, which is how every
// calendar anybody has used works. The rolling windows sit beside them as a secondary set,
// because they answer a different question: a month is the quota period, and yesterday/7/30/90
// is somebody narrowing or widening the question. Rendering them all as one strip made both
// harder to use — the eye could not tell which of two adjacent buttons changed the *kind* of
// period.
//
// Every option is free to switch to. All of them are folded out of one cached Snowflake read, so
// none of them costs a query — see `activity.ActivitySource`. Labels and tooltips come from the
// server: Yesterday's day is worked out there from `generated_at` in Pacific, never from this
// browser's clock.
function PeriodPicker({
  months,
  options,
  value,
  onChange,
}: {
  months: { key: string; label: string }[];
  options: ActivityWindowOption[];
  value: string;
  onChange: (key: string) => void;
}) {
  return (
    <>
      <div className="field">
        <span className="fieldlab">Month</span>
        <div className="winpick" role="group" aria-label="Reporting month">
          {months.map(month => (
            <button
              key={month.key}
              type="button"
              className={'winbtn' + (month.key === value ? ' on' : '')}
              aria-pressed={month.key === value}
              // "August 2026" is too wide for six buttons, so the button says "Aug" and the
              // title carries the year. The section head under it prints the full label.
              title={month.label}
              onClick={() => onChange(month.key)}
            >
              {month.label.slice(0, 3)}
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <span className="fieldlab">Rolling</span>
        <div className="winpick alt" role="group" aria-label="Rolling window">
          {options.map(option => (
            <button
              key={option.key}
              type="button"
              className={'winbtn' + (option.key === value ? ' on' : '')}
              aria-pressed={option.key === value}
              title={option.title ?? undefined}
              onClick={() => onChange(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

export function ActivityView() {
  const { getToken, email, signOut } = useAuthContext();

  // The period lives in the URL so a board somebody is looking at can be sent to somebody else.
  //
  // Empty means "whatever a fresh visit should land on", and the server answers that with the
  // month in progress. It is deliberately not resolved here: the client's idea of the current
  // month and the server's can differ across a timezone, and the server's is the one that
  // matches the data.
  const [searchParams, setSearchParams] = useSearchParams();
  const window = searchParams.get('window') || '';

  // Which drawer is open, as a key plus the number the caller printed. The total travels with
  // the key because only the caller knows it — a tile shows its own scoped figure, and the
  // drawer needs it to say "the 200 most recent of 431".
  const [openDrawer, setOpenDrawer] = useState<{ key: string; total: number } | null>(null);
  // Undefined until somebody picks a scope; until then the page opens on the viewer's own row.
  const [chosenStrategist, setSelectedStrategist] = useState<string | null | undefined>(undefined);

  // One opener for every clickable number on the page, tiles and funnel stages alike. Clicking
  // the number that is already open closes it, which is how the funnel has always behaved.
  const openList = useCallback((key: string, total: number) => {
    setOpenDrawer(current => (current?.key === key ? null : { key, total }));
  }, []);

  const activityQuery = useQuery({
    queryKey: ['activity', window],
    queryFn: () => fetchActivity(getToken, window),
    // Keep the window you were looking at on screen while the next one loads. Each window is
    // its own query key and its own Snowflake read, so without this every switch blanks the
    // whole page back to "Querying Snowflake…" -- and the first read of a cold window takes
    // about as long as the book's does. The refresh dot in the masthead already says work is
    // happening, and the highlighted button stays on the data actually being shown rather than
    // on the one that has been asked for and not arrived.
    placeholderData: keepPreviousData,
  });

  const payload = activityQuery.data;
  const selectedStrategist = chosenStrategist !== undefined
    ? chosenStrategist
    : defaultStrategist(payload?.strategists ?? [], payload?.viewer?.email);

  // The selected person's row, or null for the whole team. Every scoped number on the page comes
  // off this one object — the quota tiles, the book strip and the funnel — so there is one place
  // where "which strategist" is resolved rather than three that could disagree.
  // The Scout row is picked the same way as a person (activity/lanes.ts).
  const scopedRow = useMemo((): StrategistRow | null => {
    if (!payload) return null;
    return findScopedRow(payload, selectedStrategist);
  }, [payload, selectedStrategist]);

  // Scoping to one strategist narrows the funnel, the trend and the drawers together, so the
  // page reads as one person's board rather than as a table that happens to be filtered.
  //
  // The funnel is recomputed from that person's row rather than re-fetched: every number in it
  // is already on the row, and a round trip to Snowflake to filter four numbers would be a
  // slower way of getting the same answer.
  const scopedFunnel = useMemo((): FunnelStage[] => {
    if (!payload) return [];
    const row = scopedRow;
    if (!row) return payload.funnel;

    // Every stage keeps the unit it has unscoped: accounts on the headline, the stage's own
    // second number underneath. The two account counts are distinct counts rather than sums of
    // the meeting or deal numbers -- two meetings at one company is one account, and deriving
    // the headline from the numbers beneath it would silently switch its unit the moment
    // somebody clicked a row.
    const byKey: Record<string, Partial<FunnelStage>> = {
      reached: { accounts: row.accounts_worked, contacts: row.contacts_reached },
      replied: { accounts: row.accounts_replied, contacts: row.contacts_replied },
      met: { accounts: row.accounts_met_attended,
             booked: row.meetings_booked, attended: row.meetings_attended,
             owned: row.meetings_owned, influenced: row.meetings_influenced },
      opportunity: { accounts: row.accounts_to_stage_2, deals: row.stage_2_deals,
                     pipeline: row.qualified_pipeline },
    };
    return payload.funnel.map(stage => ({ ...stage, ...byKey[stage.key] }));
  }, [payload, scopedRow]);

  // Drawer contents follow the same scope as everything else on the page.
  const scopedMembers = useMemo(() => {
    if (!payload || !openDrawer) return [];
    const members = payload.members[openDrawer.key] ?? [];
    return scopeMembers(members, scopedRow);
  }, [payload, openDrawer, scopedRow]);

  const scopedWeeks = useMemo(() => {
    if (!payload) return [];
    return scopeWeeks(payload, selectedStrategist);
  }, [payload, selectedStrategist]);

  if (activityQuery.error instanceof AccessDeniedError) {
    return (
      <DeniedGate message={activityQuery.error.message} email={email} onSignOut={signOut} />
    );
  }

  // The open drawer's wording comes from the payload, which is where every list's name and
  // definition are written. A key with no entry cannot open anything, so a stale URL or a
  // renamed list fails closed rather than rendering a drawer with no title.
  const drawerRequest = useMemo((): DrawerRequest | null => {
    if (!payload || !openDrawer) return null;
    const definition = payload.drawers[openDrawer.key];
    if (!definition) return null;
    return { key: openDrawer.key, total: openDrawer.total, ...definition };
  }, [payload, openDrawer]);

  const body = () => {
    if (activityQuery.isPending) return <div className="loading">Querying Snowflake…</div>;
    if (activityQuery.error) {
      return (
        <div className="err">
          <b>Could not load outbound activity.</b>
          <br />
          {(activityQuery.error as Error).message}
        </div>
      );
    }
    if (!payload) return null;

    const scopedName = scopedRow ? scopedRow.sdr_owner : null;

    return (
      <>
        <SectionHead title="Outbound" count={payload.window.label} />
        <div className="controls scope">
          <PeriodPicker
            months={payload.window.months}
            options={payload.window.options}
            value={payload.window.key}
            onChange={next => {
              setOpenDrawer(null);
              setSearchParams({ window: next }, { replace: true });
            }}
          />
          <StrategistPicker
            rows={tableRows(payload)}
            value={selectedStrategist}
            onChange={next => {
              // A drawer open on one person's stage would keep describing them after the
              // scope moved, so it closes with the scope rather than re-filtering underneath.
              setOpenDrawer(null);
              setSelectedStrategist(next);
            }}
          />
        </div>

        {/* Ordered as the question is actually asked: the score first, then the accounts it
            came out of, then the meetings behind it, then who did the work — and only then the
            funnel and the trend, which are the "why" rather than the "how much". */}
        <SectionHead
          title={isScoutRow(scopedRow) ? 'Scout-sourced results'
            : scopedName ? `${scopedName.split(' ')[0]}’s quota` : 'Team quota'}
          count={payload.window.label}
          asOf={scopedName ? scopedName : 'all strategists'}
        />
        <QuotaStrip payload={payload} row={scopedRow} onOpen={openList} />

        <SectionHead title="Accounts" count={payload.window.label} />
        <BookStrip payload={payload} row={scopedRow} onOpen={openList} />

        <SectionHead title="Meetings" count={payload.window.label} />
        <MeetingStrip payload={payload} row={scopedRow} onOpen={openList} />

        <SectionHead title="By strategist" count={payload.window.label} />
        <StrategistTable
          rows={tableRows(payload)}
          minDenominator={payload.min_rate_denominator}
          selected={selectedStrategist}
          onSelect={setSelectedStrategist}
          today={payload.today}
        />
        {payload.scout_note ? <p className="covnote">{payload.scout_note}</p> : null}

        <SectionHead
          title="The funnel"
          count={`${payload.window.start} → ${payload.window.end}`}
          asOf={scopedName ? scopedName : 'all strategists'}
        />
        <Funnel
          stages={scopedFunnel}
          minDenominator={payload.min_rate_denominator}
          openStage={openDrawer?.key ?? null}
          onOpenStage={openList}
        />

        {/* The hygiene count. Under the funnel and never inside it: an account with no
            owner is out of programme by definition, so counting its work would answer a
            different question. It is here because it usually means somebody forgot to set
            ownership, which is fixable. */}
        {payload.hygiene.accounts ? (
          <p className="covnote">
            {formatCount(payload.hygiene.accounts)} account
            {payload.hygiene.accounts === 1 ? '' : 's'} worked in this period carr
            {payload.hygiene.accounts === 1 ? 'ies' : 'y'} no strategist, so{' '}
            {formatCount(payload.hygiene.sends)} send
            {payload.hygiene.sends === 1 ? '' : 's'} sit outside every number above. Setting{' '}
            <code>SDR_OWNER_C</code> on them brings them into the programme.
          </p>
        ) : null}

        <SectionHead title="Weekly" count={scopedName ?? 'all strategists'} />
        <Trend weeks={scopedWeeks} minDenominator={payload.min_rate_denominator} />
      </>
    );
  };

  return (
    <>
      <PageChrome
        freshness={payload?.freshness ?? null}
        build={payload?.build}
        viewer={payload?.viewer}
      >
        {body()}
      </PageChrome>

      <StageDrawer
        request={drawerRequest}
        members={scopedMembers}
        selfBooked={payload?.self_booked}
        attendanceEvidence={scopedVerification(payload, scopedRow?.sdr_owner_email ?? null)}
        onClose={() => setOpenDrawer(null)}
      />
    </>
  );
}
